import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { Button, Card, ProgressBar } from '../../ui';
import { ProcessorStatePanel, useProcessorStates } from '../../ProcessorStateBadge';

const STATUS_URL = '/ui/photo-id/processor/status';

const RUN_FULL_BACKLOG_CONFIRM =
  'This will send ID request emails to ALL eligible clients (never sent + needing reminders). ' +
  'This could be thousands of emails. Are you sure?';

interface BacklogProgress {
  status: string; message: string;
  sent: number; failed: number; skipped: number; total: number; percentage: number;
}

/**
 * Bespoke panel for Photo ID Request. Escalated from Task 2: the registry
 * schema (statusEndpoint/enable/disable/runActions) can express the basic
 * controls, but "Process Full Backlog" starts a server job and needs its own
 * jobId-keyed 3s-poll progress bar, which the generic schema can't express.
 * Rather than leaving the generic entry half-complete, this panel owns the
 * FULL set of controls (status/enable/disable/run + full-backlog) so there's
 * one place with full parity — see processorRegistry.ts's photo_id_request
 * comment and the task report for why this shape was chosen over a
 * generic-entry + additive-section split (the registry has no concept of
 * "generic panel plus an extra bespoke section").
 */
export const PhotoIdPanel: React.FC = () => {
  const { apiRequest } = useAuth();
  const queryClient = useQueryClient();
  const states = useProcessorStates();

  const [message, setMessage] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [backlogProgress, setBacklogProgress] = useState<BacklogProgress | null>(null);

  const statusQuery = useQuery({
    queryKey: ['photo-id-status'],
    queryFn: () => apiRequest<{ isEnabled: boolean; stats?: { totalPending: number; processedToday: number; lastRunTime?: string } }>({ url: STATUS_URL, method: 'GET' }),
  });
  const status = statusQuery.data;
  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: ['photo-id-status'] });

  const toggleProcessor = async (enable: boolean) => {
    try {
      await apiRequest({ url: `/ui/photo-id/processor/${enable ? 'enable' : 'disable'}`, method: 'POST' });
      setMessage(`ID processor ${enable ? 'enabled' : 'disabled'} successfully`);
      refreshStatus();
    } catch {
      setMessage(`Failed to ${enable ? 'enable' : 'disable'} ID processor`);
    }
  };

  const handleStartIdProcessor = async () => {
    setRunning(true);
    setMessage('Starting ID processor...');
    try {
      const res = await apiRequest<{ success: boolean; message: string; processed: number; failed: number }>({
        url: '/ui/photo-id/processor/run', method: 'POST',
      });
      if (res.success) {
        setMessage(`${res.message}. Processed: ${res.processed}, Failed: ${res.failed}`);
        refreshStatus();
      } else {
        setMessage(res.message || 'Failed to run ID processor');
      }
    } catch {
      setMessage('Failed to run ID processor');
    } finally {
      setRunning(false);
    }
  };

  const handleRunFullBacklog = async () => {
    if (!window.confirm(RUN_FULL_BACKLOG_CONFIRM)) return;
    try {
      setBacklogProgress({ status: 'starting', message: 'Starting...', sent: 0, failed: 0, skipped: 0, total: 0, percentage: 0 });
      const res = await apiRequest<{ jobId: string }>({ url: '/ui/photo-id/processor/run-full-backlog', method: 'POST' });
      setJobId(res.jobId);
    } catch {
      setBacklogProgress(null);
      setMessage('Failed to start full backlog');
    }
  };

  // Poll the job-id-keyed progress endpoint every 3s until it completes or
  // errors — a genuine dynamic-key poller the registry schema can't express,
  // ported verbatim from the old panel's useEffect.
  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const progress = await apiRequest<BacklogProgress>({
          url: `/ui/photo-id/processor/backlog-progress/${jobId}`, method: 'GET',
        });
        setBacklogProgress(progress);
        if (progress.status === 'completed' || progress.status === 'error') {
          clearInterval(interval);
          setJobId(null);
          refreshStatus();
        }
      } catch {
        // stop polling on any fetch error, matches old behaviour
        // (ProcessorsManagement.tsx's backlog-progress poller)
        clearInterval(interval);
        setJobId(null);
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const stats = status?.stats;

  return (
    <Card title="Photo ID Request" actions={
      <>
        <Button size="sm" variant="secondary" disabled={status?.isEnabled} onClick={() => toggleProcessor(true)}>Enable ID Processor</Button>
        <Button size="sm" variant="ghost" disabled={!status?.isEnabled} onClick={() => toggleProcessor(false)}>Disable ID Processor</Button>
      </>
    }>
      <p className="pc-desc">Automatically sends photo ID verification requests to clients with claims that have LOCs sent.</p>

      <ProcessorStatePanel processorId="photo_id_request" state={states.photo_id_request} />

      {stats && (
        <dl className="pc-status">
          <dt>totalPending</dt><dd>{stats.totalPending}</dd>
          <dt>processedToday</dt><dd>{stats.processedToday}</dd>
          {stats.lastRunTime && (<><dt>lastRun</dt><dd>{new Date(stats.lastRunTime).toLocaleString()}</dd></>)}
        </dl>
      )}

      <div className="pc-actions">
        <Button variant="primary" disabled={running || !status?.isEnabled} onClick={handleStartIdProcessor}>
          {running ? 'Processing...' : 'Start ID Processor'}
        </Button>
        <Button variant="secondary" onClick={refreshStatus}>Refresh Status</Button>
        <Button variant="danger" disabled={Boolean(jobId)} onClick={handleRunFullBacklog}>
          {jobId ? 'Backlog Running...' : 'Process Full Backlog'}
        </Button>
      </div>

      {message && <p className="pc-desc">{message}</p>}

      {backlogProgress && (
        <div className="pc-haiku-panel">
          <strong>
            Full Backlog: {backlogProgress.status === 'completed' ? 'Complete' : backlogProgress.status === 'error' ? 'Error' : 'Running'}
          </strong>
          <p className="pc-desc">{backlogProgress.message}</p>
          {backlogProgress.total > 0 && (
            <>
              <ProgressBar value={backlogProgress.percentage} max={100} />
              <p className="pc-desc">
                {backlogProgress.sent.toLocaleString()} sent · {backlogProgress.skipped.toLocaleString()} skipped (already verified) ·{' '}
                {backlogProgress.failed.toLocaleString()} failed · {backlogProgress.percentage}% of {backlogProgress.total.toLocaleString()} clients
              </p>
            </>
          )}
        </div>
      )}
    </Card>
  );
};
